<template>
  <button
    class="btn btn-block btn-sm bg-transparent font-bold p-0"
    type="button"
    @click="back"
  >
    <span class="flex items-center gap-2 mb-4">
      <TbArrowLeftIcon class="w-4 h-4 text-primary" />
      Back
    </span>
  </button>

  <div class="lg:w-3/4">
    <div class="primary font-semibold text-2xl mb-6">
      Edit Payments
    </div>

    <div class="flex flex-col gap-4 mb-8">
      <div class="flex justify-between items-center">
        <h4 class="text-lg font-semibold">
          Update Payment Schedule
        </h4>

        <router-link
          :to="canReview ? reviewUrl : ''"
          :aria-disabled="!canReview"
          class="btn btn-ghost btn-sm"
          :class="!canReview ? 'opacity-0 pointer-events-none' : ''"
          @click.prevent="!canReview"
        >
          Review Changes
        </router-link>
      </div>

      <Error
        v-if="error"
        :error="error"
      />

      <Table
        item-type="Payment"
        :table-id="'edit-payments'"
        :items="tableItems"
        :total-item-count="tableItems?.length || 0"
        :headers="paymentsTableHeaders"
        :count="{ singular: 'payment', plural: 'payments' }"
        hide-header
      >
        <template #tableHeader>
          <div class="table-header mb-2 flex justify-between items-center">
            <h3 class="text-lg font-bold">
              Payments
            </h3>
            <button
              :disabled="!isScheduleEdited"
              class="btn btn-ghost disabled:hover:bg-white disabled:bg-white disabled:shadow-none btn-sm flex items-center gap-2"
              :class="!isScheduleEdited ? 'opacity-0 pointer-events-none' : ''"
              @click="handleClickReset"
            >
              <TbArrowPathIcon class="w-4 h-4" />
              Reset
            </button>
          </div>
        </template>

        <template #row(dueDate)="{ item }">
          <div
            v-if="item.isEditable"
            class="py-1"
          >
            <TbDatePicker
              :date="inputsDueDates[item.installmentId]"
              :min="Date.now()"
              @selected-date="handleDateChange(item as EditingPayment, $event)"
            />
          </div>
          <div
            v-else
            class="py-2"
          >
            {{ item.formattedDueDate }}
          </div>
        </template>

        <template #row(amount)="{ item, itemIndex }">
          <div
            v-if="item.isEditable"
            class="flex items-center"
          >
            <TbInputCents
              :amount-in-cents="inputsAmountInCents[item.installmentId]"
              :currency="item.currency"
              :disabled="item.amountDisabled"
              @change:amount-in-cents="(amount: number) => emitAmountInCents(item as EditingPayment, amount)"
            />
            <Tooltip v-if="hasAdjustedAmount(itemIndex)">
              <template #trigger="{ assignReference, show, hide }">
                <div
                  :ref="(el: any) => assignReference(el)"
                  class="cursor-pointer ml-2"
                  @mouseenter="show"
                  @mouseleave="hide"
                >
                  <TbSolidInformationIcon class="w-4 h-4 text-gray-300" />
                </div>
              </template>
              <template #tooltip>
                <p v-if="!hasDiscardedChanges(itemIndex)">
                  This amount was automatically recalculated from its original amount of {{ getOriginalAmount(itemIndex)
                  }}.
                </p>
                <p v-else>
                  This amount was automatically recalculated, and your input of {{ getPreRecalculationAmount(itemIndex)
                  }} was overwritten.
                </p>
              </template>
            </Tooltip>
          </div>
          <span v-else>{{ formatCurrencyCents(item.currency, item.amountInCents) }}</span>
        </template>

        <template #row(status)="{ item }">
          <PaymentStatus :formattedStatus="item.formattedStatus" />
        </template>
      </Table>
    </div>

    <div class="flex flex-col gap-4">
      <div class="flex justify-between items-center">
        <h4 class="text-lg font-semibold">
          View Original Payment Schedule
        </h4>

        <Toggle
          :model-value="showOriginalSchedule"
          @update:model-value="toggleOriginalSchedule"
        />
      </div>

      <div v-if="showOriginalSchedule">
        <Table
          item-type="Payment"
          :items="originalSchedule"
          :total-item-count="originalSchedule.length"
          :headers="paymentsTableHeaders"
          :count="{ singular: 'payment', plural: 'payments' }"
        >
          <template #row(amount)="{ item }">
            <div class="py-3">
              <span>{{ formatCurrencyCents(item.currency, item.amountInCents) }}</span>
            </div>
          </template>

          <template #row(status)="{ item }">
            <PaymentStatus :formattedStatus="item.formattedStatus" />
          </template>
        </Table>
      </div>
    </div>

    <!-- This is a hack to make sure the page is tall enough to scroll so that the date picker is fully visible -->
    <div class="h-48" />
  </div>
  <ConfirmationDialog
    v-model="showResetConfirmation"
    title="Reset Changes"
    action="Reset"
    :fn="confirmReset"
    confirm-button-style="btn-primary"
    @cancel="closeResetConfirmation"
  >
    <template #pre>
      This will remove all changes made to the payment schedule and return it to its original state. This action cannot
      be undone.
    </template>
  </ConfirmationDialog>
</template>

<script setup lang="ts">
import Table from '@/components/table.vue';
import {
  TbArrowLeftIcon,
  TbArrowPathIcon,
  TbDatePicker,
  TbInputCents,
  TbSolidInformationIcon,
} from '@/components/tasty_bistro';
import Toggle from '@/components/toggle.vue';
import Tooltip from '@/components/tooltip.vue';
import { formatCurrencyCents } from '@/filters/currency';
import {
  computed,
  ref,
  watch,
} from 'vue';
import { useRouter, type RouteLocationRaw } from 'vue-router';

import ConfirmationDialog from '@/pages/app/project/dashboard/components/modals/confirmation.vue';

import { dateToYmd, formatDate } from '@/filters/date';
import {
  EditingPayment,
  IssueWithData,
  OrderPayment,
  ResultChangedInstallments,
} from '../types';
import Error from './error.vue';
import PaymentStatus from './payment_status.vue';

const router = useRouter();
const emit = defineEmits(['finalize', 'schedule-change', 'reset']);

const inputsAmountInCents = ref<Record<string, number>>({});
const inputsDueDates = ref<Record<string, Date>>({});

const props = defineProps<{
  isScheduleEdited: boolean;
  reviewUrl: RouteLocationRaw;
  error: unknown;
  issues: IssueWithData[];
  editingSchedule: EditingPayment[];
  originalSchedule: OrderPayment[];
  changeResult: ResultChangedInstallments;
  orderId: string;
  projectId: string;
}>();

const canReview = computed(() => props.isScheduleEdited && !props.error);

watch(() => props.editingSchedule, (newSchedule: EditingPayment[]) => {
  const newAmounts: Record<string, number> = {};
  const newDates: Record<string, Date> = {};

  newSchedule.forEach((item: EditingPayment) => {
    newAmounts[item.installmentId] = item.amountInCents;
    newDates[item.installmentId] = item.dueDate;
  });

  inputsAmountInCents.value = newAmounts;
  inputsDueDates.value = newDates;
}, {
  immediate: true,
});

type EditingTableItem = EditingPayment & {
  rowClass: string;
  id: string; // used as :key in the table component
  amountDisabled: boolean;
};

const tableItems = computed<EditingTableItem[]>(() => {
  return props.editingSchedule.map((item: EditingPayment, index: number) => {
    let rowClass = 'bg-white';
    if (!item.isEditable) {
      rowClass = 'bg-gray-100';
    } else if (hasError(item)) {
      rowClass = 'bg-[#f8d7da]';
    }
    return {
      ...item,
      id: item.installmentId,
      rowClass,
      amountDisabled: hasAdjustedAmount(index),
    };
  });
});

const showOriginalSchedule = ref(false);
const showResetConfirmation = ref(false);

const hasError = (item: EditingPayment): boolean => props.issues.some((issue) => issue.data?.installmentId === item.installmentId);

const paymentsTableHeaders = [
  {
    title: 'Date (UTC)',
    key: 'dueDate',
    formatter: (val: string) => formatDate(val, 'MMM dd, yyyy'),
    maxWidth: '80px',
  },
  {
    title: 'Amount',
    key: 'amount',
    class: 'currency',
  },
  {
    title: 'Status',
    key: 'status',
  },
];

const toggleOriginalSchedule = () => {
  showOriginalSchedule.value = !showOriginalSchedule.value;
};

const handleDateChange = (item: EditingPayment, newDate: Date) => {
  if (`${newDate}` === `${item.dueDate}`) {
    return;
  }
  emit('schedule-change', item.installmentId, 'newDueDate', dateToYmd(newDate));
};

const emitAmountInCents = (item: EditingPayment, newAmountInCents: number) => {
  emit('schedule-change', item.installmentId, 'newAmountInCents', newAmountInCents);
};

const openResetConfirmation = () => {
  showResetConfirmation.value = true;
};

const closeResetConfirmation = () => {
  showResetConfirmation.value = false;
};

const confirmReset = async () => {
  emit('reset');
  showResetConfirmation.value = false;
};

const handleClickReset = async () => {
  openResetConfirmation();
};

// Adjusted amount means it was automatically calculated by the system
// The last item is always adjusted
// If an earlier installment has its amount increased by enough, more than one
// installment could be adjusted
const hasAdjustedAmount = (itemIndex: number) => {
  if (itemIndex === props.editingSchedule.length - 1) {
    return true;
  }

  const { installmentId } = props.editingSchedule[itemIndex];
  const change = props.changeResult[installmentId];
  if (change?.amountInCents?.automaticallyAdjusted) {
    return true;
  }

  return false;
};

const hasDiscardedChanges = (itemIndex: number) => {
  return props.editingSchedule[itemIndex].changesDiscarded;
};

const getOriginalAmount = (itemIndex: number) => {
  const { amountInCents } = props.originalSchedule[itemIndex];
  return formatCurrencyCents(props.originalSchedule[itemIndex].currency, amountInCents);
};

const getPreRecalculationAmount = (itemIndex: number) => {
  const { installmentId } = props.editingSchedule[itemIndex];
  const change = props.changeResult[installmentId];
  const cents = change?.amountInCents?.oldAmountInCents;
  if (!cents) {
    return 'N/A';
  }
  return formatCurrencyCents(props.editingSchedule[itemIndex].currency, cents);
};

function back() {
  router.go(-2); // Need to return -2 because we're using history to set schedule id
}

</script>
